/**
 * Local-first persistence layer. IndexedDB is the PWA's primary source of
 * truth — every write lands here immediately and survives refresh, browser
 * restart, and total network loss. The backend API (api/main.py) is a
 * secondary sync/backup target, written to via the outbox queue in
 * lib/sync.ts whenever the app is online.
 */
import { openDB, type IDBPDatabase } from 'idb';
import { DEFAULT_WEIGHTS, type GvmRecord, type HistoryEntry, type OutboxEntry, type Weights, DIMENSIONS } from '../lib/types';
import { calculateOverallScore } from '../lib/scoring';

const DB_NAME = 'gvm-pwa';
const DB_VERSION = 1;

export interface BackupSnapshot {
  id: string; // ISO timestamp, also the primary key
  createdAt: string;
  reason: string;
  records: GvmRecord[];
  history: HistoryEntry[];
}

let dbPromise: Promise<IDBPDatabase> | null = null;

export function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const records = db.createObjectStore('records', { keyPath: 'id' });
        records.createIndex('by_type', 'type');
        records.createIndex('by_status', 'status');
        records.createIndex('by_owner', 'owner');
        records.createIndex('by_parent', 'parent_id');
        records.createIndex('by_lane', 'execution_lane');
        records.createIndex('by_archived', 'archived');
        records.createIndex('by_updated', 'updated_at');

        const history = db.createObjectStore('history', { keyPath: 'id' });
        history.createIndex('by_record', 'record_id');

        db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
        db.createObjectStore('meta', { keyPath: 'key' });
        db.createObjectStore('backups', { keyPath: 'id' });
      },
    });
  }
  return dbPromise;
}

function uuid(): string {
  if ('randomUUID' in crypto) return crypto.randomUUID();
  // Fallback for older WebViews.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function newRecordId(): string {
  // Permanent, offline-safe, globally-unique ID. Never remapped after
  // creation — this is the record's GVM ID for life, even before it ever
  // reaches the server.
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `GVM-${ts}-${rand}`;
}

export async function getWeights(): Promise<Weights> {
  const db = await getDb();
  const row = await db.get('meta', 'scoring_weights');
  return row ? (row.value as Weights) : { ...DEFAULT_WEIGHTS };
}

export async function setWeights(weights: Weights): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key: 'scoring_weights', value: weights });
  // Recalculate every record's overall_score against the new weights.
  const tx = db.transaction('records', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    const rec = cursor.value as GvmRecord;
    rec.overall_score = calculateOverallScore(
      Object.fromEntries(DIMENSIONS.map((d) => [d, (rec as any)[`score_${d}`]])),
      weights,
    );
    await cursor.update(rec);
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function getAllRecords(includeArchived = false): Promise<GvmRecord[]> {
  const db = await getDb();
  const all = (await db.getAll('records')) as GvmRecord[];
  return includeArchived ? all : all.filter((r) => !r.archived);
}

export async function getRecord(id: string): Promise<GvmRecord | undefined> {
  const db = await getDb();
  return db.get('records', id);
}

async function logHistory(recordId: string, field: string | null, oldValue: unknown, newValue: unknown, note = ''): Promise<void> {
  const db = await getDb();
  const entry: HistoryEntry = {
    id: uuid(),
    record_id: recordId,
    timestamp: new Date().toISOString(),
    field,
    old_value: oldValue === undefined || oldValue === null ? null : String(oldValue),
    new_value: newValue === undefined || newValue === null ? null : String(newValue),
    note,
  };
  await db.put('history', entry);
}

async function enqueueOutbox(op: OutboxEntry['op']): Promise<void> {
  const db = await getDb();
  await db.add('outbox', { createdAt: new Date().toISOString(), op });
}

export async function getOutbox(): Promise<OutboxEntry[]> {
  const db = await getDb();
  return db.getAll('outbox');
}

export async function outboxCount(): Promise<number> {
  const db = await getDb();
  return db.count('outbox');
}

export async function removeOutboxEntry(seq: number): Promise<void> {
  const db = await getDb();
  await db.delete('outbox', seq);
}

export interface CaptureInput {
  title: string;
  description?: string;
  type: GvmRecord['type'];
  parent_id?: string | null;
  status?: GvmRecord['status'];
  category?: string;
  owner?: string;
  execution_lane?: string;
  next_action?: string;
  deadline?: string | null;
  notes?: string;
  raw_capture?: string;
}

export async function createRecord(input: CaptureInput): Promise<GvmRecord> {
  const db = await getDb();
  const weights = await getWeights();
  const now = new Date().toISOString();
  const rec: GvmRecord = {
    id: newRecordId(),
    title: input.title,
    description: input.description ?? '',
    type: input.type,
    parent_id: input.parent_id ?? null,
    related_ids: [],
    category: input.category ?? '',
    status: input.status ?? 'inbox',
    owner: input.owner ?? '',
    execution_lane: input.execution_lane ?? '',
    priority_rank: null,
    execution_allocation_pct: null,
    score_vision_alignment: 5,
    score_impact: 5,
    score_revenue_potential: 5,
    score_market_window: 5,
    score_time_efficiency: 5,
    score_leverage: 5,
    score_evidence_readiness: 5,
    overall_score: 5,
    next_action: input.next_action ?? '',
    activation_gate: '',
    current_blocker: '',
    reactivation_trigger: '',
    deadline: input.deadline ?? null,
    notes: input.notes ?? '',
    raw_capture: input.raw_capture ?? '',
    created_at: now,
    updated_at: now,
    last_reviewed_at: null,
    archived: false,
    deleted_at: null,
  };
  rec.overall_score = calculateOverallScore(
    Object.fromEntries(DIMENSIONS.map((d) => [d, (rec as any)[`score_${d}`]])),
    weights,
  );
  await db.put('records', rec);
  await logHistory(rec.id, 'created', null, rec.title, 'Filed via capture');
  await enqueueOutbox({ kind: 'create', record: rec });
  return rec;
}

export async function updateRecord(id: string, patch: Partial<GvmRecord> & { note?: string }): Promise<GvmRecord> {
  const db = await getDb();
  const existing = await db.get('records', id);
  if (!existing) throw new Error(`Record ${id} not found`);
  const weights = await getWeights();
  const note = patch.note ?? '';
  const { note: _note, ...fieldPatch } = patch;
  const changes: Array<[string, unknown, unknown]> = [];

  for (const [key, val] of Object.entries(fieldPatch)) {
    if (val === undefined) continue;
    const oldVal = (existing as any)[key];
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      changes.push([key, oldVal, val]);
      (existing as any)[key] = val;
    }
  }
  existing.overall_score = calculateOverallScore(
    Object.fromEntries(DIMENSIONS.map((d) => [d, (existing as any)[`score_${d}`]])),
    weights,
  );
  existing.updated_at = new Date().toISOString();
  await db.put('records', existing);
  for (const [field, oldVal, newVal] of changes) {
    await logHistory(id, field, oldVal, newVal, note);
  }
  if (!changes.length && note) {
    await logHistory(id, 'note', null, null, note);
  }
  await enqueueOutbox({ kind: 'update', id, patch: fieldPatch as Partial<GvmRecord>, });
  return existing;
}

/**
 * Seeds the real current project hierarchy (task section 8) directly into
 * IndexedDB so the standalone PWA is immediately useful on a device that
 * has never talked to the backend. No-op if any records already exist
 * (idempotent — safe to call on every boot).
 */
export async function seedInitialRecordsIfEmpty(): Promise<number> {
  const db = await getDb();
  const existingCount = await db.count('records');
  if (existingCount > 0) return 0;

  const { SEED_RECORDS } = await import('../lib/seedRecords');
  const weights = await getWeights();
  const now = new Date().toISOString();
  let created = 0;

  for (const seed of SEED_RECORDS) {
    const rec: GvmRecord = {
      id: seed.id,
      title: seed.title,
      description: seed.description,
      type: seed.type,
      parent_id: null,
      related_ids: [],
      category: seed.category,
      status: seed.status,
      owner: 'William Blizzard',
      execution_lane: seed.execution_lane,
      priority_rank: seed.priority_rank,
      execution_allocation_pct: seed.execution_allocation_pct || null,
      score_vision_alignment: seed.scores.vision_alignment,
      score_impact: seed.scores.impact,
      score_revenue_potential: seed.scores.revenue_potential,
      score_market_window: seed.scores.market_window,
      score_time_efficiency: seed.scores.time_efficiency,
      score_leverage: seed.scores.leverage,
      score_evidence_readiness: seed.scores.evidence_readiness,
      overall_score: 0,
      next_action: seed.next_action ?? '',
      activation_gate: seed.activation_gate ?? '',
      current_blocker: '',
      reactivation_trigger: '',
      deadline: null,
      notes: seed.notes,
      raw_capture: '',
      created_at: now,
      updated_at: now,
      last_reviewed_at: null,
      archived: false,
      deleted_at: null,
    };
    rec.overall_score = calculateOverallScore(
      Object.fromEntries(DIMENSIONS.map((d) => [d, (rec as any)[`score_${d}`]])),
      weights,
    );
    await db.put('records', rec);
    await logHistory(rec.id, 'created', null, rec.title, 'Seeded initial project hierarchy');
    created += 1;
  }
  return created;
}

export async function archiveRecord(id: string): Promise<void> {
  await updateRecord(id, { archived: true, note: 'Soft-deleted (archived)' } as any);
  await enqueueOutbox({ kind: 'archive', id });
}

export async function restoreRecord(id: string): Promise<void> {
  await updateRecord(id, { archived: false, note: 'Restored from archive' } as any);
  await enqueueOutbox({ kind: 'restore', id });
}

/** Permanent local delete. The UI must obtain an explicit second confirmation before calling this. */
export async function hardDeleteRecord(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('records', id);
  const tx = db.transaction('history', 'readwrite');
  const idx = tx.store.index('by_record');
  let cursor = await idx.openCursor(id);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function getHistory(recordId: string): Promise<HistoryEntry[]> {
  const db = await getDb();
  const entries = await db.getAllFromIndex('history', 'by_record', recordId);
  return entries.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

// ------------------------------------------------------------- backups ---

export async function createBackupSnapshot(reason: string): Promise<BackupSnapshot> {
  const db = await getDb();
  const records = (await db.getAll('records')) as GvmRecord[];
  const history = (await db.getAll('history')) as HistoryEntry[];
  const snapshot: BackupSnapshot = {
    id: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    reason,
    records,
    history,
  };
  await db.put('backups', snapshot);
  // Keep the most recent 20 snapshots.
  const all = await db.getAllKeys('backups');
  if (all.length > 20) {
    const sorted = [...all].sort();
    for (const key of sorted.slice(0, all.length - 20)) {
      await db.delete('backups', key);
    }
  }
  return snapshot;
}

export async function listBackups(): Promise<BackupSnapshot[]> {
  const db = await getDb();
  const all = (await db.getAll('backups')) as BackupSnapshot[];
  return all.sort((a, b) => b.id.localeCompare(a.id));
}

export async function restoreBackup(id: string): Promise<void> {
  const db = await getDb();
  const snap = await db.get('backups', id) as BackupSnapshot | undefined;
  if (!snap) throw new Error(`Backup ${id} not found`);
  const tx = db.transaction(['records', 'history'], 'readwrite');
  await tx.objectStore('records').clear();
  await tx.objectStore('history').clear();
  for (const rec of snap.records) await tx.objectStore('records').put(rec);
  for (const h of snap.history) await tx.objectStore('history').put(h);
  await tx.done;
}

// -------------------------------------------------------------- import ---

export async function replaceAllRecords(records: GvmRecord[]): Promise<{ inserted: number; updated: number }> {
  const db = await getDb();
  let inserted = 0;
  let updated = 0;
  const tx = db.transaction('records', 'readwrite');
  for (const rec of records) {
    const existing = await tx.store.get(rec.id);
    await tx.store.put(rec);
    if (existing) updated += 1; else inserted += 1;
  }
  await tx.done;
  return { inserted, updated };
}

// ------------------------------------------------------------- sync meta ---

export async function getMeta(key: string): Promise<unknown> {
  const db = await getDb();
  const row = await db.get('meta', key);
  return row?.value;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key, value });
}
